/**
 * Closing Costs Calculator Utility
 * Centralized logic for calculating closing costs to avoid duplication
 */

/**
 * Calculate mansion tax based on asking price and thresholds
 */
export const calculateMansionTax = (price, thresholds) => {
  if (!thresholds || !price) return 0;

  let applicableRate = 0;
  for (const threshold of thresholds) {
    if (price >= threshold.min) {
      applicableRate = threshold.rate;
    } else {
      break;
    }
  }

  return Math.round((price * applicableRate) / 100);
};

/**
 * Calculate transfer taxes based on asking price and thresholds
 */
export const calculateTransferTaxes = (price, thresholds) => {
  if (!thresholds || !price) return 0;

  let applicableTotal = 0;
  for (const threshold of thresholds) {
    if (price >= threshold.min) {
      applicableTotal = threshold.total;
    } else {
      break;
    }
  }

  return Math.round((price * applicableTotal) / 100);
};

/**
 * Calculate mortgage recording tax
 * Based on LOAN AMOUNT, not purchase price
 *
 * Resale:
 *   If loan ≤ $500K: 1.8%
 *   If loan > $500K: 1.925%
 *
 * Sponsor:
 *   If loan ≤ $500K: 2.05%
 *   If loan > $500K: 2.175%
 *
 * @returns {Object} { amount, rate, loanAmount } for display purposes
 */
export const calculateMortgageRecordingTax = (loanAmount, config, saleType = "Resale") => {
  if (!config || !loanAmount) return { amount: 0, rate: 0, loanAmount: 0 };

  // Get rates based on sale type (resale vs sponsor)
  const saleTypeLower = (saleType || "Resale").toLowerCase();
  const rates = saleTypeLower === "sponsor" ? config.sponsor : config.resale;

  if (!rates) return { amount: 0, rate: 0, loanAmount: 0 };

  const rate = loanAmount <= config.threshold ? rates.rate_below : rates.rate_above;
  const amount = Math.round((loanAmount * rate) / 100);

  return { amount, rate, loanAmount };
};

// TIRSA Zone 2 (New York City) title insurance rates, in dollars per $1,000 of coverage.
//
// The first entry of each table is a MINIMUM premium covering the first $35,000, not a rate;
// every entry after it is the rate applied to the dollars falling inside that bracket. The
// final entry has no upper limit and applies to everything above the one before it.
//
// This replaces a flat per-$1,000 approximation that ignored the minimum, used the wrong
// rates, and charged the lender's policy at full price rather than the simultaneous-issue
// discount -- which overstated the mortgage policy roughly three-fold.
const TIRSA_ZONE_2_OWNER = [
  [35000, 382], [50000, 6.34], [100000, 5.16], [500000, 4.14], [1000000, 3.78],
  [5000000, 3.48], [10000000, 3.09], [15000000, 2.92], [null, 2.62],
];

const TIRSA_ZONE_2_LOAN = [
  [35000, 327], [50000, 5.27], [100000, 4.31], [500000, 3.46], [1000000, 3.14],
  [5000000, 2.90], [10000000, 2.57], [15000000, 2.42], [null, 2.19],
];

// TIRSA rounds to the nearest whole dollar, half up. Math.round does exactly that for the
// positive values we deal in.
const tirsaRound = (v) => Math.round(v);

/**
 * Premium before rounding, walking the brackets.
 *
 * Returns an unrounded number on purpose: the simultaneous-issue calculation below takes 30%
 * of one of these and subtracts two others, and rounding each step first would drift.
 */
const bracketedPremium = (amount, brackets) => {
  if (!amount || amount <= 0) return 0;
  if (amount <= 35000) return brackets[0][1];     // the minimum, not a rate

  let premium = brackets[0][1];
  let previous = 35000;

  for (let i = 1; i < brackets.length; i += 1) {
    const [limit, rate] = brackets[i];
    if (limit === null) {
      premium += ((amount - previous) / 1000) * rate;
      break;
    }
    const dollars = Math.min(amount, limit) - previous;
    if (dollars > 0) premium += (dollars / 1000) * rate;
    if (amount <= limit) break;
    previous = limit;
  }
  return premium;
};

/**
 * Owner's title insurance premium — TIRSA Zone 2, on the purchase price.
 */
export const calculateTitleInsurance = (price) => tirsaRound(bracketedPremium(price, TIRSA_ZONE_2_OWNER));

/**
 * Lender's (mortgage) title insurance, issued simultaneously with the owner's policy.
 *
 * TIRSA 10(B)(1): 30% of the Loan Rate for coverage up to the owner's policy amount, 100% of
 * it above. The portion above cannot be priced as a standalone policy, because the rates are
 * bracketed on the TOTAL amount insured -- it is the difference between the Loan Rate at the
 * full loan and the Loan Rate at the owner's coverage.
 *
 * Takes the purchase price as well as the loan, which the old flat-rate version did not need.
 */
export const calculateMortgageTitleInsurance = (loanAmount, purchasePrice = 0) => {
  if (!loanAmount || loanAmount <= 0) return 0;

  const owner = purchasePrice || 0;
  const first = Math.min(loanAmount, owner || loanAmount);
  let premium = bracketedPremium(first, TIRSA_ZONE_2_LOAN) * 0.30;

  if (owner && loanAmount > owner) {
    premium += bracketedPremium(loanAmount, TIRSA_ZONE_2_LOAN)
             - bracketedPremium(owner, TIRSA_ZONE_2_LOAN);
  }
  return tirsaRound(premium);
};

/**
 * Calculate total closing costs based on configuration
 * @param {Object} params - Calculation parameters
 * @returns {Object} Breakdown of closing costs
 */
export const calculateClosingCosts = ({
  askingPrice,
  buildingType = "Condo",
  saleType = "Resale",
  financing = "Yes",
  downPaymentPercent = 25, // Default 25% down payment
  config,
  editableFees = {}, // Optional: override default fees
  otherCosts = [], // Optional: additional costs array
  closingCostCredit = 0,
  monthlyHoa = 0 // Monthly common charges/HOA — drives the Sponsor working-capital default
}) => {
  if (!config) {
    throw new Error('Config is required for closing costs calculation');
  }

  const fees = config.fees || {};

  // Start with fees (use editable fees if provided, otherwise use config defaults)
  const costs = {
    applicationFee: editableFees.applicationFee ?? fees.application_fee ?? 0,
    appraisalFee: editableFees.appraisalFee ?? fees.appraisal_fee ?? 0,
    creditCheck: editableFees.creditCheck ?? fees.credit_check ?? 0,
    bankAttorney: editableFees.bankAttorney ?? fees.bank_attorney ?? 0,
    recordingFee: editableFees.recordingFee ?? fees.recording_fee ?? 0,
    attorney: editableFees.attorney ?? fees.attorney ?? 0,
    workingCapital: editableFees.workingCapital ?? fees.working_capital ?? 0,
    closerFee: editableFees.closerFee ?? fees.closer_fee ?? 0,
  };

  // Sponsor (new-development) closings carry different standard buyer fees: working capital is
  // typically ~2 months of common charges, plus a sponsor attorney and a closer fee. Apply these
  // as the Sponsor defaults (still overridable — an explicit editableFees value always wins).
  //
  // These read their OWN config keys rather than the resale ones, deliberately. The two happen
  // to be $4,000 today, but they are different fees that move for different reasons -- a
  // sponsor's attorney is set by the sponsor, a resale attorney by the buyer -- and sharing a
  // key would make one quietly follow the other the first time either changed. The literals
  // below are the fallback for a config that predates these keys, not the source of truth.
  if (saleType === "Sponsor") {
    costs.workingCapital = editableFees.workingCapital
      ?? Math.round((monthlyHoa || 0) * (fees.sponsor_working_capital_months ?? 2));
    costs.attorney = editableFees.attorney ?? fees.sponsor_attorney ?? 4000;
    costs.closerFee = editableFees.closerFee ?? fees.sponsor_closer_fee ?? 450;
  }

  // Calculate mansion tax
  costs.mansionTax = calculateMansionTax(askingPrice, config.mansion_tax?.thresholds);

  // Calculate transfer taxes (only for Sponsor sales)
  costs.transferTaxes = saleType === "Sponsor"
    ? calculateTransferTaxes(askingPrice, config.transfer_tax?.thresholds)
    : 0;

  // Normalize buildingType for comparison (backend returns lowercase)
  const buildingTypeLower = (buildingType || "").toLowerCase();

  // Calculate loan amount for mortgage recording tax
  const loanAmount = askingPrice * (1 - downPaymentPercent / 100);

  // Calculate mortgage recording tax (only for non-Coop/Condop with financing)
  // Based on LOAN AMOUNT, not purchase price
  // Rates differ for Resale vs Sponsor
  let mrtDetails = { amount: 0, rate: 0, loanAmount: 0 };
  if (buildingTypeLower !== "coop" && buildingTypeLower !== "condop" && financing === "Yes") {
    mrtDetails = calculateMortgageRecordingTax(loanAmount, config.mortgage_recording_tax?.condo, saleType);
  }
  costs.mortgageRecordingTax = mrtDetails.amount;

  // Calculate title insurance (only for non-Coop/Condop)
  // Uses tiered TIRSA rate table based on purchase price
  costs.titleInsurance = (buildingTypeLower !== "coop" && buildingTypeLower !== "condop")
    ? calculateTitleInsurance(askingPrice)
    : 0;

  // Calculate mortgage title insurance (only for non-Coop/Condop with financing)
  // Based on LOAN AMOUNT, simultaneous issue rate: $2.75 per $1,000
  costs.mortgageTitleInsurance = (buildingTypeLower !== "coop" && buildingTypeLower !== "condop" && financing === "Yes")
    ? calculateMortgageTitleInsurance(loanAmount, askingPrice)
    : 0;

  // Calculate totals
  const totalFees = Object.values(costs).reduce((sum, value) => sum + value, 0);
  const totalOtherCosts = otherCosts.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalBeforeCredit = totalFees + totalOtherCosts;
  const netClosingCosts = totalBeforeCredit - closingCostCredit;

  return {
    costs,
    totalFees,
    totalOtherCosts,
    totalBeforeCredit,
    closingCostCredit,
    netClosingCosts: Math.round(netClosingCosts),
    mrtDetails // { amount, rate, loanAmount } for display
  };
};

/**
 * Calculate default closing costs (for initial page load)
 * Assumes defaults: Resale, financing = Yes
 */
export const calculateDefaultClosingCosts = async (propertyId, askingPrice, buildingType, authFetch) => {
  try {
    const response = await authFetch(`/config/closing_costs?property_id=${propertyId}`);

    if (!response || !response.data) {
      console.warn('No closing costs config found');
      return 0;
    }

    const result = calculateClosingCosts({
      askingPrice,
      buildingType,
      saleType: "Resale", // Default to Resale
      financing: "Yes", // Default to Yes (most common)
      config: response.data,
      closingCostCredit: 0
    });

    return result.netClosingCosts;
  } catch (error) {
    console.error('Error calculating default closing costs:', error);
    return 0;
  }
};
